import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container, Grid,
  Typography, Button, Box, Select, MenuItem, FormControl, Chip,
  CircularProgress, TextField, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';

const HomePage = () => {
  const { user } = useAuth();
  const loadingRef = useRef(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { addToCart } = useCart();
  const [firstLoad , setFirstLoad] = useState(true);
  // Fetch initial data
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Adds smooth scrolling animation
    });
    const fetchInitialData = async () => {
      try {
        const [categoriesRes] = await Promise.all([
          api.get('/categories')
        ]);
        
        setCategories(categoriesRes.data);
        await fetchProducts(1, true); // Load first page
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

// Fetch products function
const fetchProducts = async (page, reset = false, category = '', search = '') => {
  // hard lock
  if (loadingRef.current) return;
  loadingRef.current = true;

  const setSpinner = reset ? setLoading : setLoadingMore;
  setSpinner(true);

  try {
    const params = new URLSearchParams({
      page,
      limit: 8,
      ...(category && { categoryId: category }),
      ...(search && { search }),
    });

    const { data } = await api.get(`/products?${params}`);

    setProducts(prev =>
      reset ? data.products : [...prev, ...data.products]
    );
    setCurrentPage(data.pagination.currentPage);
    setTotalPages(data.pagination.totalPages);
    setHasMore(data.pagination.hasNext);
  } catch (err) {
    console.error('Error fetching products:', err);
  } finally {
    setSpinner(false);
    loadingRef.current = false;   // release the lock
    setFirstLoad(false);
  }
};


  // Handle category change
  const handleCategoryChange = async (event) => {
    const categoryId = event.target.value;
    setSelectedCategory(categoryId);
    setCurrentPage(1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Adds smooth scrolling animation
    });
    await fetchProducts(1, true, categoryId, searchQuery);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle search submission
  const handleSearchSubmit = async (event) => {
    if (event.key === 'Enter' || event.type === 'click') {
      setCurrentPage(1);
      await fetchProducts(1, true, selectedCategory, searchQuery);
    }
  };

  // Reset search
  const handleSearchReset = async () => {
    setSearchQuery('');
    setCurrentPage(1);
    await fetchProducts(1, true, selectedCategory, '');
  };

  // Infinite scroll handler
  const handleScroll = useCallback(async() => {
    const scrollTop = document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;
    
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (!loadingMore && hasMore) {
        var nextPage = 1 ;
        if(firstLoad) {
          setFirstLoad(false);
          nextPage = 1; 
        } else {
          nextPage = currentPage + 1;
        }
        await fetchProducts(nextPage, false, selectedCategory, searchQuery);
      }
    }
  }, [currentPage, loadingMore, hasMore, selectedCategory, searchQuery, firstLoad]);

  // Add scroll event listener
  useEffect(() => {
    let ticking = false;
    
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll);
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [handleScroll]);

  // Reset pagination when category or search changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
  }, [selectedCategory, searchQuery]);

  if (loading && products.length === 0) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>جار التحميل ...</Typography>
      </Container>
    );
  }

  return (
    <div>
      
<Box sx={{ 
  position: 'sticky',
  top: 64, // Adjust this value based on your public action bar height
  zIndex: 1100, // Ensure it stays above other content
  backgroundColor: 'background.paper',
  py: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
  mb: 4
}}>
  {/* Horizontal Scroll Category Filter */}
  <Box sx={{ 
    display: 'flex', 
    gap: 1, 
    overflowX: 'auto',
    maxWidth: '100%',
    py: 1,
    px: 1,
    '&::-webkit-scrollbar': {
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#c1c1c1',
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#a8a8a8',
    }
  }}>
    {/* All Categories Button */}
    <Chip
      label="جميع الأقسام"
      onClick={() => handleCategoryChange({ target: { value: '' } })}
      variant={selectedCategory === '' ? 'filled' : 'outlined'}
      color={selectedCategory === '' ? 'primary' : 'default'}
      sx={{ 
        flexShrink: 0,
        minWidth: 'fit-content',
        fontWeight: selectedCategory === '' ? 'bold' : 'normal'
      }}
    />
    
    {/* Category Chips */}
    {categories.map((category) => (
      <Chip
        key={category.id}
        label={category.name}
        onClick={() => handleCategoryChange({ target: { value: category.id } })}
        variant={selectedCategory === category.id ? 'filled' : 'outlined'}
        color={selectedCategory === category.id ? 'primary' : 'default'}
        sx={{ 
          flexShrink: 0,
          minWidth: 'fit-content',
          fontWeight: selectedCategory === category.id ? 'bold' : 'normal'
        }}
      />
    ))}
  </Box>
</Box>
    <Container sx={{ py: 4, mb: 10 }}>
      {/* Search Box - Centered at Top */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 4,
        width: '100%'
      }}>
        <TextField
          placeholder="ابحث باسم المنتج أو الوصف..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={handleSearchSubmit}
          sx={{ 
            width: '100%',
            maxWidth: '600px',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon 
                  color="action" 
                  onClick={handleSearchSubmit}
                  sx={{ cursor: 'pointer' }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {products.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6">
            لايوجد منتجات {selectedCategory || searchQuery ? ' تطابق معايير البحث' : ''}
          </Typography>
          {(selectedCategory || searchQuery) && (
            <Button 
              variant="outlined" 
              sx={{ mt: 2 }}
              onClick={handleSearchReset}
            >
              إعادة تعيين البحث
            </Button>
          )}
        </Box>
      ) : (
        <>
  <Grid container spacing={{ xs: 2, md: 3 }} justifyContent="center">
        {products.map((product) => {
          return (
            <ProductCard 
              key={`${product.id}-${product.created_at}`}
              product={product}
              addToCart={addToCart}
            />
          );
        })}
  </Grid>

          {/* Loading indicator for infinite scroll */}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>جار تحميل المنتجات ...</Typography>
            </Box>
          )}

          {/* No more products message */}
          {!hasMore && products.length > 0 && (
            <Typography variant="body2" textAlign="center" sx={{ mt: 4, color: 'text.secondary' }}>
              لايوجد المزيد من المنتجات
            </Typography>
          )}
        </>
      )}
    </Container>
        </div>
  );
};

export default HomePage;